/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/Materials/SubmissionDetail/AdminSubmissionDetail.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";
import SubmissionDetailLayout from "../../../../../components/submission/SubmissionDetailLayout";
import "./AdminSubmissionDetail.css";
import AdminStartReviewModal from "../AdminStartReviewModal";
import {
  canAdminApproveDataset,
} from "../../../../../constants/reviewStatus";
import { adminApproveSubmission } from "../../../../../services/submissionService";

type AnyObj = { [k: string]: any };

interface Props {
  submissionId: string;
  onBack: () => void;
}

const AdminSubmissionDetail: React.FC<Props> = ({ submissionId, onBack }) => {
  const [submission, setSubmission] = useState<AnyObj>(null as unknown as AnyObj);
  const [loading, setLoading] = useState(true);
  const [startReviewOpen, setStartReviewOpen] = useState(false);
  const [approving, setApproving] = useState(false);

  const loadSubmission = () => {
    setLoading(true);
    axiosClient
      .get(`/submissions/${submissionId}`, {
        params: { includeFormFields: "true" },
      })
      .then((res) => {
        if (res.data.success) setSubmission(res.data.data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadSubmission();
  }, [submissionId]);

  if (loading) return <div className="admin-loading">Loading submission…</div>;
  if (!submission) return <div className="admin-empty">Submission not found.</div>;

  const reviewStatusRaw =
    submission?.reviewStatus ?? submission?.review_status ?? "PENDING";
  const materialId = submission?.background?.materialId ?? null;

  const handleApproveDataset = async () => {
    if (!canAdminApproveDataset(reviewStatusRaw)) return;
    if (
      !window.confirm(
        `Approve dataset ${submission.submissionCode ?? submissionId}? This marks the submission as approved.`
      )
    ) {
      return;
    }
    setApproving(true);
    try {
      const res = await adminApproveSubmission(submissionId);
      if (res.data?.success === false) {
        alert(res.data?.message || "Approval failed.");
        return;
      }
      loadSubmission();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Approval failed. Please try again.";
      alert(msg);
    } finally {
      setApproving(false);
    }
  };

  return (
    <>
      <SubmissionDetailLayout
        bannerTitle="Submission Details"
        submission={submission}
        onBack={onBack}
        headerActions={
          <>
            <button className="primary-btn" onClick={() => setStartReviewOpen(true)}>Flag Dataset</button>
            {canAdminApproveDataset(reviewStatusRaw) && (
              <button
                type="button"
                className="primary-btn"
                style={{ marginLeft: "4px", backgroundColor: "#2e7d32", borderColor: "#2e7d32" }}
                disabled={approving}
                onClick={() => void handleApproveDataset()}
              >
                {approving ? "Approving…" : "Approve Dataset"}
              </button>
            )}
          </>
        }
      />

      {submission && (
        <AdminStartReviewModal
          open={startReviewOpen}
          onClose={() => setStartReviewOpen(false)}
          submissionId={submissionId}
          submissionCode={submission.submissionCode ?? submission.id}
          materialId={materialId}
          onReviewStarted={() => {
            loadSubmission();
          }}
        />
      )}
    </>
  );
};

/** React component — Admin Submission Detail. */
export default AdminSubmissionDetail;
